import React, { useState } from 'react';
import { PhotoViewerModal } from './PhotoViewer';
import styles from './Feed.module.css';

/**
 * 참고 앱(fe_server3) 홈이 실제로 쓰는 형태들.
 *
 * 그쪽 홈은 카드 하나로 다 하지 않고, 내용의 무게에 따라 형태를 바꿉니다.
 *   HeroBanner  가장 중요한 한 건 — 화면 폭 사진 + 흰 글씨
 *   SeriesCard  둘러보는 것 — 대형 세로 사진 레일
 *   TopicBlock  읽히는 것 — 넓은 배너 + 아래 제목/설명
 *   WideCard    목록 — 좌 세로 사진 + 우 정보
 *   BorderCard  할 일 — 테두리 카드 + 하단 메타줄 + 액션
 *
 * 치수·행간은 참고 소스 값 그대로, 색만 우리 토큰입니다.
 */

/* ══ Tag ══ */

export type FeedTagTone = 'default' | 'brand' | 'line' | 'photo' | 'danger';

const toneClass: Record<FeedTagTone, string> = {
    default: '',
    brand: styles.tagBrand,
    line: styles.tagLine,
    photo: styles.tagOnPhoto,
    danger: styles.tagDanger,
};

export const FeedTag = ({ tone = 'default', children }: { tone?: FeedTagTone; children: React.ReactNode }) => (
    <span className={`${styles.tag} ${toneClass[tone]}`.trim()}>{children}</span>
);

export interface FeedTagItem {
    label: string;
    tone?: FeedTagTone;
}

const Tags = ({ items, className }: { items?: FeedTagItem[]; className?: string }) =>
    items && items.length > 0 ? (
        <span className={className}>
            {items.map(tg => <FeedTag key={tg.label} tone={tg.tone}>{tg.label}</FeedTag>)}
        </span>
    ) : null;

/* ══ FeedTitle — 섹션 제목 ══ */

interface FeedTitleProps {
    children: React.ReactNode;
    /** 제목 옆 강조 한 마디 (개수 등) */
    note?: React.ReactNode;
    /** 제목 아래 한 줄 설명 */
    sub?: React.ReactNode;
    moreLabel?: string;
    onMore?: () => void;
}

export const FeedTitle = ({ children, note, sub, moreLabel, onMore }: FeedTitleProps) => (
    <>
        <h2 className={styles.title}>
            {children}
            {note ? <span className={styles.titleNote}>{note}</span> : null}
            {moreLabel && onMore ? (
                <button type="button" className={styles.titleMore} onClick={onMore}>{moreLabel}</button>
            ) : null}
        </h2>
        {sub ? <span className={styles.titleSub}>{sub}</span> : null}
    </>
);

/* ══ HeroBanner ══ */

interface HeroBannerProps {
    imageUrl?: string | null;
    /** 좌상단 태그 — 첫 번째는 채움, 나머지는 테두리 */
    tags?: FeedTagItem[];
    title: string;
    /** 제목 아래 한 줄 (장소 등) */
    paragraph?: string;
    /** 그 아래 굵은 한 줄 (일시 등) */
    date?: string;
    /** 우하단 정사각 썸네일 (최대 3장). 누르면 사진 팝업이 열립니다 */
    photos?: string[];
    onClick?: () => void;
    /** 사진 대신 넣을 것 (지도 등) */
    children?: React.ReactNode;
    /** 배너 아래 붙는 액션 버튼들 */
    actions?: React.ReactNode;
}

export const HeroBanner = ({
    imageUrl, tags, title, paragraph, date, photos, onClick, children, actions,
}: HeroBannerProps) => {
    // 배너에 얹는 썸네일은 세 장까지. 그 이상은 팝업에서 넘겨 봅니다.
    const [viewerOpen, setViewerOpen] = useState(false);
    const [broken, setBroken] = useState<string[]>([]);
    const shown = (photos || []).filter(p => p && !broken.includes(p));
    const thumbs = shown.slice(0, 3);

    return (
    <>
        <div className={styles.hero} onClick={onClick} role={onClick ? 'button' : undefined}>
            {imageUrl ? <img className={styles.heroBg} src={imageUrl} alt="" /> : children}
            <span className={styles.heroScrim} />
            <span className={styles.heroInner}>
                {tags && tags.length > 0 && (
                    <span className={styles.heroTags}>
                        {tags.map((tg, i) => (
                            <span
                                key={tg.label}
                                className={i === 0 ? styles.heroTag : `${styles.heroTag} ${styles.heroTagOutline}`}
                            >
                                {tg.label}
                            </span>
                        ))}
                    </span>
                )}
                <span className={styles.heroBottom}>
                    <span className={styles.heroText}>
                        <strong className={styles.heroTitle}>{title}</strong>
                        {paragraph ? <span className={styles.heroParagraph}>{paragraph}</span> : null}
                        {date ? <span className={styles.heroDate}>{date}</span> : null}
                    </span>
                    {thumbs.length > 0 && (
                        <span className={styles.heroThumbs}>
                            {thumbs.map((src, i) => (
                                <img
                                    key={src + i}
                                    className={styles.heroThumb}
                                    src={src}
                                    alt=""
                                    loading="lazy"
                                    /* 배너 전체가 눌리는 자리라 여기서 전파를 끊습니다 */
                                    onClick={(e) => { e.stopPropagation(); setViewerOpen(true); }}
                                    /* 링크가 깨진 사진은 빈 네모로 남기지 않고 치웁니다 */
                                    onError={() => setBroken(prev => (prev.includes(src) ? prev : [...prev, src]))}
                                />
                            ))}
                        </span>
                    )}
                </span>
            </span>
        </div>
        {actions ? <div className={styles.heroActions}>{actions}</div> : null}
        <PhotoViewerModal
            open={viewerOpen}
            photos={shown}
            alt={title}
            onClose={() => setViewerOpen(false)}
        />
    </>
    );
};

/* ══ SeriesCard — 대형 세로 사진 카드 ══ */

export const SeriesRail = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.seriesRail}>{children}</div>
);

interface SeriesCardProps {
    imageUrl?: string | null;
    /** 좌상단 번호 */
    count?: React.ReactNode;
    title: string;
    sub?: string;
    tags?: FeedTagItem[];
    onClick?: () => void;
}

export const SeriesCard = ({ imageUrl, count, title, sub, tags, onClick }: SeriesCardProps) => (
    <button type="button" className={styles.series} onClick={onClick}>
        {imageUrl ? <img className={styles.seriesImg} src={imageUrl} alt="" loading="lazy" /> : null}
        <span className={styles.seriesScrim} />
        {count != null ? <span className={styles.seriesCount}>{count}</span> : null}
        <span className={styles.seriesText}>
            <span className={styles.seriesTitle}>{title}</span>
            {sub ? <span className={styles.seriesSub}>{sub}</span> : null}
        </span>
        <Tags items={tags?.map(tg => ({ ...tg, tone: tg.tone || 'photo' }))} className={styles.seriesTag} />
    </button>
);

/* ══ TopicBlock — 넓은 배너 + 아래 텍스트 ══ */

interface TopicBlockProps {
    imageUrl?: string | null;
    tags?: FeedTagItem[];
    /** 배너 우하단 보조 정보 */
    meta?: string;
    title: string;
    sub?: string;
    onClick?: () => void;
}

export const TopicBlock = ({ imageUrl, tags, meta, title, sub, onClick }: TopicBlockProps) => (
    <button type="button" className={styles.topic} onClick={onClick}>
        <span className={styles.topicBanner}>
            {imageUrl ? <img className={styles.topicImg} src={imageUrl} alt="" loading="lazy" /> : null}
            <span className={styles.topicScrim} />
            <Tags items={tags?.map(tg => ({ ...tg, tone: tg.tone || 'photo' }))} className={styles.topicTags} />
            {meta ? <span className={styles.topicMeta}>{meta}</span> : null}
        </span>
        <span className={styles.topicText}>
            <span className={styles.topicTitle}>{title}</span>
            {sub ? <span className={styles.topicSub}>{sub}</span> : null}
        </span>
    </button>
);

/* ══════════════════════════════════════════════════════════════════
 * PhotoStrip — 사진 위에 글을 얹은 낮은 배너
 *
 * 메뉴 한 줄 성격인데 TopicBlock(210/375)을 쓰면 화면의 3분의 1을 먹습니다.
 * 글은 참고의 Banner·SeriesCard 처럼 사진 위에 얹고, 높이만 120/375 로 낮춥니다.
 * ════════════════════════════════════════════════════════════════ */

interface PhotoStripProps {
    imageUrl?: string | null;
    /** 좌상단 라벨 */
    tag?: string;
    title: string;
    sub?: string;
    onClick?: () => void;
}

export const PhotoStrip = ({ imageUrl, tag, title, sub, onClick }: PhotoStripProps) => (
    <button type="button" className={styles.strip} onClick={onClick}>
        {imageUrl ? <img className={styles.stripImg} src={imageUrl} alt="" loading="lazy" /> : null}
        <span className={styles.stripScrim} />
        <span className={styles.stripText}>
            {tag ? <span className={styles.stripTag}>{tag}</span> : null}
            <span className={styles.stripTitle}>{title}</span>
            {sub ? <span className={styles.stripSub}>{sub}</span> : null}
        </span>
    </button>
);

/* ══ WideCard — 좌 세로 사진 + 우 정보 ══ */

export const WideList = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.wideList}>{children}</div>
);

interface WideCardProps {
    imageUrl?: string | null;
    /** 사진이 없을 때 자리에 놓을 글자 (제목 첫 글자 등) */
    fallback?: string;
    tags?: FeedTagItem[];
    title: string;
    sub?: string;
    /** 아래쪽 보조 줄 — 좌측 */
    foot?: React.ReactNode;
    /** 아래쪽 보조 줄 — 우측 (D-day 등) */
    footEnd?: React.ReactNode;
    onClick?: () => void;
}

export const WideCard = ({ imageUrl, fallback, tags, title, sub, foot, footEnd, onClick }: WideCardProps) => (
    <button type="button" className={styles.wide} onClick={onClick}>
        <span className={styles.wideImg}>
            {imageUrl
                ? <img src={imageUrl} alt="" loading="lazy" />
                : <span className={styles.wideImgEmpty}>{fallback}</span>}
        </span>
        <span className={styles.wideBody}>
            <span>
                <Tags items={tags} className={styles.wideTags} />
                <span className={styles.wideTitle}>{title}</span>
                {sub ? <span className={styles.wideSub}>{sub}</span> : null}
            </span>
            {(foot || footEnd) ? (
                <span className={styles.wideFoot}>
                    {foot ? <span className={styles.wideFootDate}>{foot}</span> : <span />}
                    {footEnd ? <span className={styles.wideFootEnd}>{footEnd}</span> : null}
                </span>
            ) : null}
        </span>
    </button>
);

/* ══ BorderCard — 테두리 카드 + 하단 메타줄 + 액션 ══ */

export const BorderList = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.borderList}>{children}</div>
);

interface BorderCardProps {
    tags?: FeedTagItem[];
    title: string;
    sub?: string;
    /** 하단 메타줄 — 좌측 */
    meta?: React.ReactNode;
    /** 하단 메타줄 — 우측 */
    metaEnd?: React.ReactNode;
    /** 카드 맨 아래 버튼들 */
    actions?: React.ReactNode;
    onClick?: () => void;
}

export const BorderCard = ({ tags, title, sub, meta, metaEnd, actions, onClick }: BorderCardProps) => (
    <div className={styles.border}>
        <button type="button" className={styles.borderMain} onClick={onClick}>
            <Tags items={tags} className={styles.borderTags} />
            <span className={styles.borderTitle}>{title}</span>
            {sub ? <span className={styles.borderSub}>{sub}</span> : null}
        </button>
        {(meta || metaEnd) ? (
            <div className={styles.meta}>
                {meta}
                {metaEnd ? <span className={styles.metaEnd}>{metaEnd}</span> : null}
            </div>
        ) : null}
        {actions ? <div className={styles.borderActions}>{actions}</div> : null}
    </div>
);

/** 메타줄만 따로 쓸 때 */
export const MetaRow = ({ children, end }: { children?: React.ReactNode; end?: React.ReactNode }) => (
    <div className={styles.meta}>
        {children}
        {end ? <span className={styles.metaEnd}>{end}</span> : null}
    </div>
);
